import { Button } from 'components/atoms';
import { MagellanPopups } from 'components/molecules';
import { FormikHelpers } from 'formik';
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PiVideo, PiFileText } from 'react-icons/pi';
import { PopupActions } from 'reactjs-popup/dist/types';
import { useBulkAssignVideoContracts } from 'services/channel';
import { IBulkAssignVideoContracts } from 'services/channel/types/query';
import { useParamId } from 'hooks/useParamId';
import { object, array, string, number } from 'yup';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import VideoSelectionStep from './BulkAssignVideoContractsPopup/VideoSelectionStep';
import ContractSelectionStep from './BulkAssignVideoContractsPopup/ContractSelectionStep';

export enum BulkAssignStep {
  VideoSelection = 0,
  ContractSelection = 1,
}

interface IBulkAssignFormValues {
  selectedVideoIds: string[];
  videoContracts: Array<{
    contractId: string;
    divisionPercentage: number;
    startDate: string;
    labelId?: string;
    labelName?: string;
  }>;
}

const initialValues: IBulkAssignFormValues = {
  selectedVideoIds: [],
  videoContracts: [],
};

const BulkAssignVideoContractsPopup = () => {
  const channelId = useParamId('channel');
  const [currentStep, setCurrentStep] = useState<BulkAssignStep>(
    BulkAssignStep.VideoSelection
  );
  const [formValues, setFormValues] = useState<IBulkAssignFormValues>(initialValues);
  const { t } = useTranslation();

  const bulkAssign = useBulkAssignVideoContracts();

  useEffect(() => {
    const bottomButtons = document.querySelector(
      '.magellan-popup-bottom-buttons'
    );
    if (bottomButtons) {
      const backButton = bottomButtons.querySelector(
        'button[type="button"]'
      ) as HTMLButtonElement;
      const submitButton = bottomButtons.querySelector(
        'button[type="submit"]'
      ) as HTMLButtonElement;
      const submitText = submitButton?.querySelector('span');
      const clearButton = bottomButtons.querySelector(
        'button.clear.only-icon'
      ) as HTMLButtonElement;

      if (clearButton) {
        clearButton.remove();
      }

      if (backButton) {
        backButton.disabled = currentStep === BulkAssignStep.VideoSelection;
      }
      if (submitButton) {
        submitButton.disabled =
          currentStep === BulkAssignStep.VideoSelection &&
          formValues.selectedVideoIds.length === 0;
      }
      if (submitText) {
        submitText.textContent =
          currentStep === BulkAssignStep.VideoSelection
            ? t('button.next')
            : t('button.save');
      }
    }
  }, [currentStep, formValues.selectedVideoIds, t]);

  useEffect(() => {
    const removeClearButton = () => {
      const bottomButtons = document.querySelector(
        '.magellan-popup-bottom-buttons'
      );
      if (bottomButtons) {
        const clearButton = bottomButtons.querySelector(
          'button.clear.only-icon'
        ) as HTMLButtonElement;
        if (clearButton) {
          clearButton.remove();
        }
      }
    };

    removeClearButton();

    const interval = setInterval(removeClearButton, 100);

    return () => clearInterval(interval);
  }, []);

  const onSubmit = async (
    values: IBulkAssignFormValues,
    formikHelpers: FormikHelpers<IBulkAssignFormValues>,
    actions: PopupActions | null
  ) => {
    if (currentStep === BulkAssignStep.VideoSelection) {
      setCurrentStep(BulkAssignStep.ContractSelection);
      return;
    }

    const payload: IBulkAssignVideoContracts = {
      channelId,
      videoIds: values.selectedVideoIds,
      videoContracts: values.videoContracts
        .filter(contract => contract.contractId && contract.contractId !== '')
        .map(({ labelId, labelName, ...contract }) => contract),
    };

    try {
      await bulkAssign.mutateAsync(payload, {
        onSuccess: () => {
          actions?.close();
          formikHelpers.resetForm();
          setCurrentStep(BulkAssignStep.VideoSelection);
        },
      });
    } catch (error) {
    }
  };

  const validationSchema = useMemo(() => {
    if (currentStep === BulkAssignStep.VideoSelection) {
      return object().shape({
        selectedVideoIds: array()
          .of(string())
          .min(1, t('formik-error.required')),
      });
    } else {
      return object().shape({
        selectedVideoIds: array().of(string()).required(),
        videoContracts: array()
          .of(
            object().shape({
              labelId: string()
                .required(t('formik-error.required'))
                .test('not-empty', t('formik-error.required'), value => value !== '' && value !== undefined),
              contractId: string()
                .required(t('formik-error.required'))
                .test('not-empty', t('formik-error.required'), value => value !== '' && value !== undefined),
              divisionPercentage: number()
                .required(t('formik-error.required'))
                .test('not-null', t('formik-error.required'), value => value !== null && value !== undefined)
                .test('greater-than-zero', t('formik-error.required'), value => value !== null && value !== undefined && value > 0)
                .min(0.01, t('formik-error.min'))
                .max(100, t('formik-error.max')),
              startDate: string()
                .required(t('formik-error.required'))
                .test('not-empty', t('formik-error.required'), value => value !== '' && value !== undefined),
            })
          )
          .min(1, t('formik-error.required')),
      });
    }
  }, [currentStep, t]);

  const getStepHeader = () => {
    if (currentStep === BulkAssignStep.VideoSelection) {
      return (
        <>
          <PiVideo /> {t('channel.bulkAssign.step1.title')}
        </>
      );
    } else {
      return (
        <>
          <PiFileText /> {t('channel.bulkAssign.step2.title')}
        </>
      );
    }
  };


  return (
    <MagellanPopups.Form
      header={getStepHeader()}
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={onSubmit}
      onClose={() => {
        setCurrentStep(BulkAssignStep.VideoSelection);
        setFormValues(initialValues);
      }}
      submitButton={
        <div className='flex justify-between w-full'>
          <Button
            type='button'
            isClear
            disabled={currentStep === BulkAssignStep.VideoSelection}
            onClick={() => setCurrentStep(BulkAssignStep.VideoSelection)}>
            <Button.Text text={t('button.back')} />
          </Button>
          <Button
            type={currentStep === BulkAssignStep.VideoSelection ? 'button' : 'submit'}
            variant={BUTTON_VARIANT.submit}
            disabled={
              currentStep === BulkAssignStep.VideoSelection &&
              formValues.selectedVideoIds.length === 0
            }
            onClick={currentStep === BulkAssignStep.VideoSelection ? () => {
              if (formValues.selectedVideoIds.length > 0) {
                setCurrentStep(BulkAssignStep.ContractSelection);
              }
            } : undefined}>
            <Button.Text
              text={
                currentStep === BulkAssignStep.VideoSelection
                  ? t('button.next')
                  : t('button.save')
              }
            />
          </Button>
        </div>
      }
      trigger={
        <Button type='button'>
          <Button.Text text={t('channel.bulkAssign.button')} />
        </Button>
      }>
      {({ values, setFieldValue, errors }) => {
        if (
          JSON.stringify(formValues.selectedVideoIds) !==
            JSON.stringify(values.selectedVideoIds) ||
          JSON.stringify(formValues.videoContracts) !==
            JSON.stringify(values.videoContracts)
        ) {
          setFormValues(values);
        }

        return (
          <div className='flex flex-col gap-4 w-full' style={{
            width: '950px',
            height: '600px',
            maxWidth: '950px',
            maxHeight: '600px',
          }}>
            {currentStep === BulkAssignStep.VideoSelection ? (
              <VideoSelectionStep
                channelId={channelId}
                selectedVideoIds={values.selectedVideoIds}
                onSelectionChange={(ids) =>
                  setFieldValue('selectedVideoIds', ids)
                }
              />
            ) : (
              <ContractSelectionStep
                channelId={channelId}
                videoContracts={values.videoContracts}
                onContractsChange={(contracts) => {
                  setFieldValue('videoContracts', contracts, false);
                }}
              />
            )}
          </div>
        );
      }}
    </MagellanPopups.Form>
  );
};

export default BulkAssignVideoContractsPopup;

